import { useContext, useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import {
  ROUTES,
  SALE_STATUS,
  KOT_MESSAGES,
  KEYPAD_BUTTONS,
  INVOICE_FORMATS,
  PAYMENT_MODES,
  UPI_MODES,
  KEYBOARD_KEYS,
  SCANNER_RESTRICTED_INPUTS,
  INVOICE_ACTIONS,
  DEFAULT_PAYMENT_MODE_ORDER,
  PAYMENT_MODE_ORDER_KEYS,
  EVENTS,
  STAFF_PERMISSION,
  SPECIAL_PAYMENT_MODE_ID,
  ORDER_TYPES,
} from "const";
import { eventsEmitter } from "utils/events";
import { Checkbox } from "@mui/material";
import { roundOff } from "helpers/NumberFormatHelper";
import { TerminalContext } from "context/TerminalContext";
import styles from "./styles.module.css";
import ShopsHelper from "helpers/ShopsHelper";
import * as InvoiceActionHelper from "helpers/InvoiceActionHelper";
import SalesContext from "context/SalesContext";
import ThreeInchInvoice from "components/utils/PrintInvoice/ThreeInchInvoice";
import KOTInvoice from "components/utils/PrintInvoice/KOTInvoice";
import ShopContext from "context/ShopContext";
import CustomerContext from "context/CustomerContext";
import KotManager from "helpers/KotManager";
import ProductContext from "context/ProductContext";
import CloseIcon from "images/svg/close-circle.svg";
import toast from "react-hot-toast";
import BalanceDialog from "./BalanceDialog";
import EditSaleDescriptionDialog from "../EditSaleDescriptionDialog/EditSaleDescriptionDialog";
import CashIcon from "images/svg/cash.svg";
import GpayIcon from "images/svg/gpay.svg";
import PhonePayIcon from "images/svg/phonePay.svg";
import CardsIcon from "images/svg/card.svg";
import DiscountIcon from "images/svg/discount.svg";
import PersonalCart from "images/svg/personalcard.svg";
import CustomerSelector from "components/utils/CustomerSelector/CustomerSelector";
import CheckoutActionMenu from "components/utils/CheckoutActionMenu/CheckoutActionMenu";
import BackIcon from "images/svg/back.svg";
import GeneratePaymentQrDialog from "../GeneratePaymentQrDialog/GeneratePaymentQrDialog";
import A4Invoice from "components/utils/PrintInvoice/A4Invoice";
import A5Invoice from "components/utils/PrintInvoice/A5Invoice";
import validityExpired from "components/utils/validityExpired";
import * as SalesHelper from "helpers/SalesHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as EditedSaleHelper from "helpers/EditedSaleHelper";
import * as ShiftHelper from "helpers/ShiftHelper";
import { usePermission } from "hooks/usePermission";
import { amountRoundOff } from "helpers/NumberFormatHelper";

const paymentIcons = {
  [PAYMENT_MODES.CASH]: CashIcon,
  [PAYMENT_MODES.UPI]: GpayIcon,
  [PAYMENT_MODES.GPAY]: GpayIcon,
  [PAYMENT_MODES.PHONEPAY]: PhonePayIcon,
  [PAYMENT_MODES.CARD]: CardsIcon,
  [PAYMENT_MODES.DISCOUNT]: DiscountIcon,
  [PAYMENT_MODES.DISCOUNT_PERCENTAGE]: DiscountIcon,
};

function createCustomerPaymentMode({ name, amount, icon, id, orderKey, defaultOrder, orderObj }) {
  return {
    name,
    amount,
    initialAmount: amount,
    icon,
    id,
    order: orderObj && orderObj[orderKey] !== undefined ? orderObj[orderKey] : defaultOrder,
  };
}

function SaleEditDialog({ handleClose, saleLocalId, onDone, isEditingPaymentModeOnly = false }) {
  const navigate = useNavigate();
  const {
    sales,
    activeSale: contextActiveSale,
    getSaleById,
    handleClearActiveSale,
    handleChangeSelectedCustomer,
    selectedPriceCategory,
    handleChangePriceCategory,
    chargeGroupsAppliedForSale,
    addChargeGroupToActiveSale,
    setActiveSale,
  } = useContext(SalesContext);
  const { selectedShop, paymentModes: contextPaymentModes } = useContext(ShopContext) || {};
  const { customers, updateCustomerAfterSale } = useContext(CustomerContext);
  const { showTokenNumber, localTokenNumber, setLocalToken } = useContext(TerminalContext);
  const { categories, priceCategories, chargeGroups, charges } = useContext(ProductContext);

  const [fetchedSale, setFetchedSale] = useState(null);

  const activeSale = useMemo(() => {
    if (contextActiveSale && String(contextActiveSale.local_id) === String(saleLocalId)) {
      return contextActiveSale;
    }
    return sales.find((s) => s.local_id === saleLocalId) || fetchedSale;
  }, [sales, saleLocalId, fetchedSale, contextActiveSale]);

  const [paymentModes, setPaymentModes] = useState([]);
  const [activeMode, setActiveMode] = useState(null);
  const [printSale, setPrintSale] = useState(activeSale);
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [balancePayments, setBalancePayments] = useState([]);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [showPriceCategoryDropdown, setShowPriceCategoryDropdown] = useState(false);
  const [showChargeGroupDropdown, setShowChargeGroupDropdown] = useState(false);
  const [showPaymentQrDialog, setShowPaymentQrDialog] = useState(false);
  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [pendingFinalizeAction, setPendingFinalizeAction] = useState(null);
  const [keypadString, setKeypadString] = useState("");
  const [orderTypes, setOrderTypes] = useState([]);
  const [activeOrderType, setActiveOrderType] = useState(null);
  const [kotData, setKotData] = useState({});
  const [isProcessingKOT, setIsProcessingKOT] = useState(false);
  const { checkPermissionAndExecute, PermissionDialog } = usePermission();
  const [isFinalizing, setIsFinalizing] = useState(false);
  const isFinalizingRef = useRef(false);
  const printComponentRef = useRef(null);
  const kotPrintRef = useRef(null);
  const priceCategoryDropdownRef = useRef(null);
  const chargeGroupDropdownRef = useRef(null);
  const isInitialized = useRef(false);
  const paymentTabsRef = useRef(null);

  useEffect(() => {
    if (activeMode && paymentTabsRef.current) {
      const activeEl = paymentTabsRef.current.querySelector(`.${styles.activeTab}`);
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  }, [activeMode]);

  const hasCustomerSupport = !validityExpired(selectedShop?.customer_support_validity);

  const sortedPaymentModes = useMemo(() => {
    const orderObj = selectedShop?.config?.payment_mode_order;
    const getOrder = (mode) => {
      const lower = mode.name.toLowerCase();
      if (orderObj && orderObj[lower] !== undefined) {
        return Number(orderObj[lower]);
      }
      if (mode.order !== undefined && mode.order !== null) {
        return Number(mode.order);
      }
      // Fallback default sorting:
      if (lower === "cash") return 0;
      if (lower === "credit") return DEFAULT_PAYMENT_MODE_ORDER.CREDIT;
      if (lower === "points" || lower === "loyalty points") return DEFAULT_PAYMENT_MODE_ORDER.POINTS;
      if (lower === "card") return 3;
      if (lower === "upi") return 4;
      if (lower === "discount") return DEFAULT_PAYMENT_MODE_ORDER.DISCOUNT;
      if (lower === "discount %") return DEFAULT_PAYMENT_MODE_ORDER.DISCOUNT_PERCENTAGE;
      return 5;
    };
    return [...paymentModes].sort((a, b) => getOrder(a) - getOrder(b));
  }, [paymentModes, selectedShop]);

  useEffect(() => {
    const fetchSale = async () => {
      if (saleLocalId && !sales.find((s) => s.local_id === saleLocalId)) {
        try {
          const sale = await getSaleById(saleLocalId);
          if (sale) {
            setFetchedSale({
              ...sale,
              previous_payable: sale.payable,
            });
          }
        } catch (e) {
          console.error("Error fetching sale in SaleEditDialog", e);
        }
      }
    };
    fetchSale();
  }, [saleLocalId, sales, getSaleById]);

  const activeCustomerDetails = useMemo(() => {
    if (!activeSale?.customer) return null;
    return customers.find((c) => c.id === activeSale.customer.id) || activeSale.customer;
  }, [activeSale?.customer, customers]);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: printComponentRef,
    documentTitle: `Invoice-${printSale?.bill_no || activeSale?.bill_no || "NA"}`,
    setPrintSale,
  });

  const grossPayable = useMemo(() => {
    return (activeSale?.payable ?? 0) + (activeSale?.extraDiscount ?? 0);
  }, [activeSale]);

  const { tender, discount, due, change } = useMemo(() => {
    let tenderSum = 0;
    let flatDiscount = 0;
    let percentRate = 0;

    for (const m of paymentModes) {
      if (m.id === SPECIAL_PAYMENT_MODE_ID.DISCOUNT) {
        flatDiscount = Number(m.amount) || 0;
      } else if (m.id === SPECIAL_PAYMENT_MODE_ID.DISCOUNT_PERCENTAGE) {
        percentRate = Number(m.amount) || 0;
      } else {
        tenderSum += Number(m.amount) || 0;
      }
    }

    const percentDiscount = (grossPayable * percentRate) / 100;
    const totalDiscount = NumberFormatHelper.amountRoundOff(flatDiscount + percentDiscount);
    const totalDeduction = tenderSum + totalDiscount;

    return {
      tender: tenderSum,
      discount: totalDiscount,
      due: Math.max(grossPayable - totalDeduction, 0),
      change: Math.max(totalDeduction - grossPayable, 0),
    };
  }, [paymentModes, grossPayable]);

  useEffect(() => {
    if (change <= 0 && balancePayments.length > 0) {
      setBalancePayments([]);
    }
  }, [change, balancePayments]);

  const upiAmount = useMemo(() => {
    return paymentModes
      .filter((m) => UPI_MODES.some((upiMode) => upiMode.toLowerCase() === m.name.toLowerCase()))
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
  }, [paymentModes]);

  const currentShiftId = useMemo(() => ShiftHelper.getCurrentShiftId(), []);

  const removeCustomerFromSaleLocal = async () => {
    await handleChangeSelectedCustomer(null);
    setPrintSale((p) => {
      const newp = { ...(p || {}) };
      delete newp.customer;
      return newp;
    });
  };

  const balancePaymentModeLabel = useMemo(() => {
    const activeBalanceModes = (balancePayments || []).filter((p) => p.amount > 0);
    if (activeBalanceModes.length > 0) {
      return activeBalanceModes.map((p) => p.name).join(", ");
    }
    return contextPaymentModes?.[0]?.name;
  }, [balancePayments, contextPaymentModes]);

  const handleCustomerSelection = async (customer) => {
    await handleChangeSelectedCustomer(customer);
    setPrintSale((p) => ({ ...(p || {}), customer }));
    setShowCustomerSelector(false);
  };

  const handleKOT = useCallback(async () => {
    if (isProcessingKOT) return { updatedItems: activeSale?.items || [], token_no: activeSale?.token_no };
    setIsProcessingKOT(true);

    const result = await KotManager.processKOT({
      activeSale,
      categories,
      showTokenNumber,
      localTokenNumber,
      fallbackPrintFunction: handleWebInvoicePrint,
    });

    try {
      if (!result.success) {
        if (result.message === KOT_MESSAGES.FAILED) toast.error(result.message);
        return activeSale?.items || [];
      }

      if (result.message === KOT_MESSAGES.NO_CHANGES) {
        return { updatedItems: result.updatedItems, token_no: activeSale?.token_no };
      }

      setKotData(result.saleToPrint);

      if (result.shouldIncrementToken) {
        setLocalToken((prev) => prev + 1);
      }

      const updatedSale = {
        ...activeSale,
        items: result.updatedItems,
        deletedItems: [],
        token_no: result.token_no,
        kot_status: result.kot_status,
      };
      await SalesHelper.saveSaleInDB(updatedSale);

      return { updatedItems: result.updatedItems, token_no: result.token_no };
    } catch (err) {
      toast.error("Failed to print KOT.");
      return { updatedItems: activeSale?.items || [], token_no: activeSale?.token_no };
    } finally {
      setIsProcessingKOT(false);
    }
  }, [
    activeSale,
    categories,
    showTokenNumber,
    localTokenNumber,
    handleWebInvoicePrint,
    setLocalToken,
    isProcessingKOT,
  ]);

  const onFinalizationComplete = useCallback(async () => {
    await handleClearActiveSale();
    handleClose();
    onDone?.();

    const terminalSettings = TerminalHelper.getTerminalSettings();
    if (terminalSettings?.isTableDashboardEnabled) {
      navigate(ROUTES.TABLE_DASHBOARD, { state: { refresh: true } });
    }
  }, [handleClearActiveSale, handleClose, onDone, navigate]);

  /* =================================================
   SALE FINALIZATION HELPER
   ================================================= */
  function calculateFinalPaymentDetails(activeSale, paymentModes, selectedShop) {
    let pointsCurrency = 0;
    let currentTender = 0;
    let flatDiscount = 0;
    let percentRate = 0;

    for (const m of paymentModes) {
      if (m.name === PAYMENT_MODES.POINTS) {
        pointsCurrency = Number(m.amount) || 0;
      }
      if (m.id === SPECIAL_PAYMENT_MODE_ID.DISCOUNT) {
        flatDiscount = Number(m.amount) || 0;
      } else if (m.id === SPECIAL_PAYMENT_MODE_ID.DISCOUNT_PERCENTAGE) {
        percentRate = Number(m.amount) || 0;
      } else {
        currentTender += Number(m.amount) || 0;
      }
    }

    const conversionRate = Number(selectedShop?.loyalty_point_conversion_rate || 1);
    const loyaltyPointsUsed = pointsCurrency / conversionRate;

    const grossPayable = (activeSale?.payable ?? 0) + (activeSale?.extraDiscount ?? 0);
    const currentDiscount = NumberFormatHelper.amountRoundOff(flatDiscount + (grossPayable * percentRate) / 100);
    const totalDeduction = currentTender + currentDiscount;
    const currentDue = Math.max(grossPayable - totalDeduction, 0);
    const currentChange = Math.max(totalDeduction - grossPayable, 0);

    return {
      pointsCurrency,
      loyaltyPointsUsed,
      currentDiscount,
      currentDue,
      currentChange,
    };
  }

  function prepareSaleForFinalization(activeSale, overrideItems) {
    if (!overrideItems) {
      return activeSale;
    }

    return {
      ...activeSale,
      items: overrideItems,
      deletedItems: [],
    };
  }

  const buildFinalizedSale = useCallback(
    async ({
      activeSale,
      overrideItems,
      paymentModes,
      balancePayments,
      activeCustomerDetails,
      currentDiscount,
      currentDue,
      currentChange,
      activeOrderType,
      currentShiftId,
      finalTokenNo,
      loyaltyPointsUsed,
      pointsCurrency,
      charges,
      previousCreditUsed,
    }) => {
      const sale = prepareSaleForFinalization(activeSale, overrideItems);

      return SalesHelper.buildUpdatedSale({
        sale,
        paymentModes,
        balancePayments: balancePayments,
        selectedCustomer: activeCustomerDetails,
        discount: currentDiscount,
        due: currentDue,
        change: currentChange,
        orderType: activeOrderType,
        shift: currentShiftId,
        token_no: finalTokenNo,
        loyaltyPointsUsed,
        loyaltyPointsAmount: pointsCurrency,
        charges,
        previousCreditUsed,
      });
    },
    [],
  );

  const constructFinalizedSalePayload = useCallback(
    async ({
      activeSale,
      options,
      paymentModes,
      balancePayments,
      activeCustomerDetails,
      activeOrderType,
      currentShiftId,
      charges,
      selectedShop,
      originalSale,
    }) => {
      const { overrideItems = null, finalTokenNo = null } = options;

      const { pointsCurrency, loyaltyPointsUsed, currentDiscount, currentDue, currentChange } =
        calculateFinalPaymentDetails(activeSale, paymentModes, selectedShop);

      const previousCreditUsed = Number(originalSale?.customer_credit_used || 0);

      const updatedSale = await buildFinalizedSale({
        activeSale,
        overrideItems,
        paymentModes,
        balancePayments: currentChange > 0 ? balancePayments : [],
        activeCustomerDetails,
        currentDiscount,
        currentDue,
        currentChange,
        activeOrderType,
        currentShiftId,
        finalTokenNo,
        loyaltyPointsUsed,
        pointsCurrency,
        charges,
        previousCreditUsed,
      });

      const finalSale = {
        ...updatedSale,
        payment_modes: (updatedSale.payment_modes || []).filter(
          (paymentMode) => paymentMode.name !== PAYMENT_MODES.POINTS,
        ),
        remark: updatedSale?.remark || "",
      };

      return finalSale;
    },
    [buildFinalizedSale],
  );

  const resolveFinalTokenNumber = useCallback(
    (options = {}) => {
      const { token_no = null } = options;
      if (showTokenNumber && !token_no && !activeSale?.token_no) {
        setLocalToken((prev) => prev + 1);
        return localTokenNumber;
      }
      return token_no;
    },
    [showTokenNumber, activeSale, localTokenNumber, setLocalToken],
  );

  const executePostFinalizeBackgroundTasks = useCallback(
    async (finalSale, originalSale) => {
      try {
        await updateCustomerAfterSale(finalSale, finalSale.sync_status, originalSale);
        await SalesHelper.syncSaleAndPending(finalSale);
      } catch (error) {
        console.error("Background persistence fail:", error);
      }
    },
    [updateCustomerAfterSale],
  );

  const processFinalizedSale = useCallback(
    async (options) => {
      const originalSale = activeSale?.editing_sale_local_id
        ? await SalesHelper.getSaleById(activeSale.editing_sale_local_id)
        : isEditingPaymentModeOnly && saleLocalId
          ? await SalesHelper.getSaleById(saleLocalId)
          : null;

      const finalSale = await constructFinalizedSalePayload({
        activeSale,
        options: { ...options },
        paymentModes,
        balancePayments,
        activeCustomerDetails,
        activeOrderType,
        currentShiftId,
        charges,
        selectedShop,
        originalSale,
      });

      const { invoiceAction, description } = options;

      const tempDraftLocalId = activeSale?.editing_sale_local_id ? activeSale.local_id : null;

      if (originalSale) {
        if (activeSale?.editing_sale_local_id) {
          finalSale.local_id = originalSale.local_id;
          finalSale.bill_no = originalSale.bill_no;
          finalSale.bare_bill_no = originalSale.bare_bill_no;
          if (originalSale.id) {
            finalSale.id = originalSale.id;
          } else {
            delete finalSale.id;
          }
          finalSale.status = SALE_STATUS.PAID;
          delete finalSale.editing_sale_local_id;
          delete finalSale.original_sale_id;
          delete finalSale.original_sale_bill_no;
        }

        EditedSaleHelper.attachEditedSaleRemark(originalSale, finalSale, description);
      }

      // Creating edited sale log for
      if (originalSale && finalSale?.bill_no) {
        await EditedSaleHelper.saveEditedSale(finalSale, currentShiftId?.id, description);
      }

      // TODO: set this only if default printing
      setPrintSale(finalSale);

      const savedSale = await SalesHelper.saveAndProcess(finalSale, {
        shop: selectedShop,
        fallbackPrintFunction: handleWebInvoicePrint,
        invoiceAction,
      });

      if (tempDraftLocalId) {
        await SalesHelper.deleteSale(tempDraftLocalId, { notify: true });
      }

      onFinalizationComplete();

      executePostFinalizeBackgroundTasks(savedSale, originalSale);

      return savedSale;
    },
    [
      activeSale,
      paymentModes,
      activeCustomerDetails,
      selectedShop,
      isEditingPaymentModeOnly,
      activeOrderType,
      balancePayments,
      charges,
      constructFinalizedSalePayload,
      currentShiftId,
      executePostFinalizeBackgroundTasks,
      handleWebInvoicePrint,
      onFinalizationComplete,
      saleLocalId,
    ],
  );

  // Deprecated
  const finalizeSale = useCallback(
    async (options = {}) => {
      if (isFinalizingRef.current) {
        console.warn("Sale finalization already in progress.");
        return null;
      }
      isFinalizingRef.current = true;
      setIsFinalizing(true);

      const { valid } = SalesHelper.validateSaleFinalization(activeSale, due, paymentModes, activeCustomerDetails);
      if (!valid) {
        isFinalizingRef.current = false;
        setIsFinalizing(false);
        return null;
      }

      try {
        const finalTokenNo = resolveFinalTokenNumber(options);
        const finalSale = await processFinalizedSale({ ...options, finalTokenNo });
        return finalSale;
      } catch (error) {
        console.error("Sale finalization failed:", error);
        toast.error(error?.message || "Failed to finalize sale.");
        isFinalizingRef.current = false;
        setIsFinalizing(false);
        return null;
      }
    },
    [activeSale, paymentModes, activeCustomerDetails, due, processFinalizedSale, resolveFinalTokenNumber],
  );

  function isPayableReduced(sale) {
    const isEditedSale = Boolean(sale?.editing_sale_local_id);
    const oldPayable = sale?.previous_payable;
    const newPayable = Number(sale?.payable ?? 0);
    return isEditedSale && oldPayable !== null && newPayable < oldPayable;
  }

  const createSale = useCallback(
    (invoiceAction) => async () => {
      if (isFinalizingRef.current) {
        console.warn("Sale finalization already in progress.");
        return null;
      }
      isFinalizingRef.current = true;
      setIsFinalizing(true);

      // Check for automatic KOT
      const terminalSettings = TerminalHelper.getTerminalSettings();
      if (!!terminalSettings.kotWithInvoice) {
        const kotResult = await handleKOT();
        if (kotResult?.updatedItems) {
          activeSale.items = kotResult.updatedItems;
        }
        if (kotResult?.token_no) {
          activeSale.token_no = kotResult.token_no;
        }
      }

      // Validate input
      const { valid } = SalesHelper.validateSaleFinalization(activeSale, due, paymentModes, activeCustomerDetails);
      if (!valid) {
        isFinalizingRef.current = false;
        setIsFinalizing(false);
        return null;
      }

      try {
        const options = { invoiceAction };
        options.finalTokenNo = activeSale.token_no || resolveFinalTokenNumber();

        if (isPayableReduced(activeSale)) {
          isFinalizingRef.current = false;
          setIsFinalizing(false);
          setPendingFinalizeAction(options);
          setShowDescriptionDialog(true);
          return null;
        }

        const finalSale = await processFinalizedSale(options);
        return finalSale;
      } catch (error) {
        console.error("Sale finalization failed:", error);
        toast.error(error?.message || "Failed to finalize sale.");
        isFinalizingRef.current = false;
        setIsFinalizing(false);
        return null;
      }
    },
    [activeCustomerDetails, activeSale, due, paymentModes, processFinalizedSale, resolveFinalTokenNumber, handleKOT],
  );

  const handleConfirmDescription = useCallback(
    async (description) => {
      setShowDescriptionDialog(false);
      const actionToExecute = pendingFinalizeAction;
      setPendingFinalizeAction(null);
      if (!actionToExecute) return;

      if (isFinalizingRef.current) return;
      isFinalizingRef.current = true;
      setIsFinalizing(true);

      try {
        await processFinalizedSale({
          ...actionToExecute,
          description,
        });
      } catch (error) {
        console.error("Sale finalization failed:", error);
        toast.error(error?.message || "Failed to finalize sale.");
      } finally {
        isFinalizingRef.current = false;
        setIsFinalizing(false);
      }
    },
    [pendingFinalizeAction, processFinalizedSale],
  );

  const handleCloseDescriptionDialog = useCallback(() => {
    setShowDescriptionDialog(false);
    setPendingFinalizeAction(null);
  }, []);

  const onKotAndPrintClick = useCallback(async () => {
    await createSale(INVOICE_ACTIONS.WITH_KOT)();
  }, [createSale]);

  const handleKeypadInput = useCallback(
    (val) => {
      let amountStr = SalesHelper.computeKeypadString(val, keypadString);
      let parsedAmount = parseFloat(amountStr) || 0;

      const {
        valid,
        finalAmount,
        amountStr: clampedStr,
      } = SalesHelper.validateAndClampPaymentModeAmount(
        activeMode,
        parsedAmount,
        amountStr,
        activeCustomerDetails,
        paymentModes,
        selectedShop,
        activeSale,
      );

      if (!valid) return;

      setKeypadString(clampedStr);
      setPaymentModes((prevModes) =>
        prevModes.map((mode) => {
          if (mode.name !== activeMode) return mode;
          return {
            ...mode,
            amount: amountRoundOff(finalAmount),
          };
        }),
      );
    },
    [activeCustomerDetails, activeMode, activeSale, paymentModes, keypadString, selectedShop],
  );

  const handleQrPaymentReceived = async () => {
    const currentUpiAmount = upiAmount;
    const finalUpiAmount = currentUpiAmount > 0 ? currentUpiAmount : Math.max(grossPayable - (tender + discount), 0);
    const updatedModes = InvoiceActionHelper.getUpdatedPaymentModesWithQr(paymentModes, finalUpiAmount, UPI_MODES);

    setPaymentModes(updatedModes);
    const finalSale = await finalizeSale({
      overrideItems: activeSale.items,
      overridePaymentModes: updatedModes,
      customStatus: SALE_STATUS.PAID,
    });
    if (finalSale) {
      try {
        await SalesHelper.saveAndProcess(finalSale, {
          shop: selectedShop,
          fallbackPrintFunction: handleWebInvoicePrint,
          action: INVOICE_ACTIONS.PRINT,
        });
      } catch (err) {
        console.error("QR print failed:", err);
      } finally {
        onFinalizationComplete();
      }
    }
  };

  const displayPaymentQr = useCallback(() => {
    const validation = InvoiceActionHelper.validatePaymentQr(due);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    setShowPaymentQrDialog(true);
  }, [due]);

  const handleOrderTypeChange = async (orderType) => {
    const currentOrderType = activeOrderType || activeSale?.order_type || null;
    const nextOrderType = currentOrderType === orderType ? null : orderType;

    if (nextOrderType === activeSale?.order_type && nextOrderType === activeOrderType) {
      return;
    }

    setActiveOrderType(nextOrderType);

    try {
      const isNC = nextOrderType === ORDER_TYPES.NC;
      const targetPaymentModes = isNC ? paymentModes.map((pm) => ({ ...pm, amount: 0 })) : paymentModes;

      if (isNC) {
        setPaymentModes(targetPaymentModes);
        setKeypadString("");
      }

      const originalSale = activeSale?.editing_sale_local_id
        ? await SalesHelper.getSaleById(activeSale.editing_sale_local_id)
        : isEditingPaymentModeOnly && saleLocalId
          ? await SalesHelper.getSaleById(saleLocalId)
          : null;

      const previousCreditUsed = Number(originalSale?.customer_credit_used || 0);

      const updatedSale = await SalesHelper.buildUpdatedSale({
        sale: activeSale,
        paymentModes: targetPaymentModes,
        balancePayments: balancePayments,
        selectedCustomer: activeCustomerDetails,
        discount: isNC ? 0 : discount,
        due: isNC ? 0 : due,
        change: isNC ? 0 : change,
        shift: currentShiftId,
        orderType: nextOrderType,
        charges,
        previousCreditUsed,
      });

      setActiveSale(updatedSale);
      await SalesHelper.saveSaleInDB(updatedSale, { notify: true });
    } catch (err) {
      console.error("Failed to update order type:", err);
      toast.error("Failed to update order type");
    }
  };

  const invoiceFormat = SalesHelper.getInvoicePrinterFormat();

  const InvoiceComponent = useMemo(() => {
    if (invoiceFormat === INVOICE_FORMATS.A4) {
      return <A4Invoice ref={printComponentRef} sale={printSale} shop={selectedShop} />;
    } else if (invoiceFormat === INVOICE_FORMATS.A5) {
      return <A5Invoice ref={printComponentRef} sale={printSale} shop={selectedShop} />;
    } else {
      return <ThreeInchInvoice ref={printComponentRef} sale={printSale} shop={selectedShop} />;
    }
  }, [invoiceFormat, printSale, selectedShop]);

  const handleAutoFillDue = () => {
    handleKeypadInput(`+${due}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (priceCategoryDropdownRef.current && !priceCategoryDropdownRef.current.contains(event.target)) {
        setShowPriceCategoryDropdown(false);
      }
      if (chargeGroupDropdownRef.current && !chargeGroupDropdownRef.current.contains(event.target)) {
        setShowChargeGroupDropdown(false);
      }
    };
    const unsubscribeFn = eventsEmitter.on(EVENTS.MOUSE_DOWN, handleClickOutside);
    return unsubscribeFn;
  }, []);

  useEffect(() => {
    try {
      const normalized = TerminalHelper.loadOrderTypesFromStorage();
      setOrderTypes(normalized);

      const targetOrderType = activeSale?.order_type;
      if (targetOrderType && normalized.includes(targetOrderType)) {
        setActiveOrderType((prev) => (prev === targetOrderType ? prev : targetOrderType));
      } else if (!targetOrderType) {
        setActiveOrderType((prev) => (prev === null ? prev : null));
      }
    } catch (err) {
      console.error("Failed to initialize order types", err);
    }
  }, [activeSale?.order_type]);

  const handlePaymentModeSelect = useCallback(
    (mode) => {
      const exec = () => {
        setActiveMode(mode.name);
        setKeypadString(mode.amount !== 0 ? String(mode.amount) : "");
      };

      if (mode.name === PAYMENT_MODES.DISCOUNT || mode.name === PAYMENT_MODES.DISCOUNT_PERCENTAGE) {
        checkPermissionAndExecute(STAFF_PERMISSION.DISCOUNT, exec, "Discount Access");
      } else {
        exec();
      }
    },
    [checkPermissionAndExecute],
  );

  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (isFinalizing) return;
      const isSubDialogOpen = showBalanceDialog || showCustomerSelector || showPaymentQrDialog || showDescriptionDialog;
      if (isSubDialogOpen) return;

      if (
        event.target &&
        (SCANNER_RESTRICTED_INPUTS.includes(event.target.tagName) || event.target.isContentEditable)
      ) {
        return;
      }

      if (event.key === KEYBOARD_KEYS.ENTER) {
        event.preventDefault();
        if (
          due > 0 &&
          tender === 0 &&
          activeMode &&
          activeMode !== PAYMENT_MODES.DISCOUNT &&
          activeMode !== PAYMENT_MODES.DISCOUNT_PERCENTAGE
        ) {
          setPaymentModes((prevModes) =>
            prevModes.map((mode) => (mode.name !== activeMode ? mode : { ...mode, amount: due })),
          );
          setKeypadString(due.toString());
        } else {
          await createSale(INVOICE_ACTIONS.DEFAULT)();
        }
        return;
      }

      if (event.key === KEYBOARD_KEYS.ESCAPE) {
        event.preventDefault();
        handleClose();
        return;
      }

      if (
        SalesHelper.handleFunctionKeysNavigation(event, {
          onDefaultActionBtnClick: createSale(INVOICE_ACTIONS.DEFAULT),
          onSaveInvoiceClick: createSale(INVOICE_ACTIONS.SAVE),
          onKotAndPrintClick,
          onSaveAndWhatsAppClick: createSale(INVOICE_ACTIONS.SAVE_AND_WHATSAPP),
          onDirectPrintClick: createSale(INVOICE_ACTIONS.PRINT),
        })
      ) {
        return;
      }

      if (SalesHelper.handleArrowKeysNavigation(event, sortedPaymentModes, activeMode, handlePaymentModeSelect)) {
        return;
      }

      SalesHelper.handleKeypadKeysInput(event, activeMode, handleKeypadInput);
    };
    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [
    createSale,
    onKotAndPrintClick,
    showBalanceDialog,
    showCustomerSelector,
    showPaymentQrDialog,
    showDescriptionDialog,
    isFinalizing,
    activeMode,
    handleKeypadInput,
    paymentModes,
    handlePaymentModeSelect,
    handleClose,
    sortedPaymentModes,
    due,
    tender,
  ]);

  useEffect(() => {
    if (contextPaymentModes && contextPaymentModes.length > 0) {
      if (isInitialized.current) return;

      if (saleLocalId && !activeSale) return;

      const existingChangeModes = (activeSale?.payment_modes || [])
        .filter((pm) => Number(pm.amount) < 0)
        .map((pm) => ({
          ...pm,
          amount: Math.abs(Number(pm.amount)),
        }));
      if (existingChangeModes.length > 0) {
        setBalancePayments(existingChangeModes);
      }

      const modesWithAmounts = contextPaymentModes.map((mode) => {
        const existingPayment = (activeSale?.payment_modes || []).find(
          (paymentMode) => paymentMode.name === mode.name && Number(paymentMode.amount) > 0,
        );
        const existingAmount = existingPayment ? Number(existingPayment.amount) : 0;
        return {
          ...mode,
          amount: existingAmount,
          initialAmount: existingAmount,
          icon: paymentIcons[(mode.name || "").toLowerCase()] || null,
        };
      });

      const orderObj = selectedShop?.config?.payment_mode_order;
      const discountOrder =
        orderObj && orderObj[PAYMENT_MODE_ORDER_KEYS.DISCOUNT] !== undefined
          ? orderObj[PAYMENT_MODE_ORDER_KEYS.DISCOUNT]
          : DEFAULT_PAYMENT_MODE_ORDER.DISCOUNT;

      modesWithAmounts.push({
        name: PAYMENT_MODES.DISCOUNT,
        id: SPECIAL_PAYMENT_MODE_ID.DISCOUNT,
        amount: activeSale?.extraDiscount || 0,
        icon: DiscountIcon,
        order: discountOrder,
      });

      const discountPercentageOrder =
        orderObj && orderObj[PAYMENT_MODE_ORDER_KEYS.DISCOUNT_PERCENTAGE] !== undefined
          ? orderObj[PAYMENT_MODE_ORDER_KEYS.DISCOUNT_PERCENTAGE]
          : DEFAULT_PAYMENT_MODE_ORDER.DISCOUNT_PERCENTAGE;

      modesWithAmounts.push({
        name: PAYMENT_MODES.DISCOUNT_PERCENTAGE,
        id: SPECIAL_PAYMENT_MODE_ID.DISCOUNT_PERCENTAGE,
        amount: 0,
        icon: DiscountIcon,
        order: discountPercentageOrder,
      });

      setPaymentModes((prev) => {
        // Keep CREDIT/POINTS if already added by the other useEffect
        const customerModes = prev.filter((m) => m.name === PAYMENT_MODES.CREDIT || m.name === PAYMENT_MODES.POINTS);
        return [...modesWithAmounts, ...customerModes];
      });

      if (modesWithAmounts.length > 0) {
        const activeTenderMode = modesWithAmounts.find((m) => m.amount > 0) || modesWithAmounts[0];
        setActiveMode(activeTenderMode.name);
        setKeypadString(activeTenderMode.amount !== 0 ? String(activeTenderMode.amount) : "");
      }
      isInitialized.current = true;
    }
  }, [contextPaymentModes, activeSale, saleLocalId, selectedShop?.config?.payment_mode_order]);

  useEffect(() => {
    if (activeSale?.customer?.id) {
      setPaymentModes((prev) => {
        let next = [...prev];
        const orderObj = selectedShop?.config?.payment_mode_order;
        if (!next.some((m) => m.name === PAYMENT_MODES.CREDIT)) {
          const existing = (activeSale?.payment_modes || []).find((pm) => pm.name === PAYMENT_MODES.CREDIT);
          const creditAmount = existing ? Number(existing.amount) : Number(activeSale?.customer_credit_used || 0);
          next.push(
            createCustomerPaymentMode({
              name: PAYMENT_MODES.CREDIT,
              amount: creditAmount,
              icon: CashIcon,
              id: SPECIAL_PAYMENT_MODE_ID.CREDIT,
              orderKey: PAYMENT_MODE_ORDER_KEYS.CREDIT,
              defaultOrder: DEFAULT_PAYMENT_MODE_ORDER.CREDIT,
              orderObj,
            }),
          );
        }
        if (!next.some((m) => m.name === PAYMENT_MODES.POINTS)) {
          const existing = (activeSale?.payment_modes || []).find((pm) => pm.name === PAYMENT_MODES.POINTS);
          const pointsAmount = existing ? Number(existing.amount) : Number(activeSale?.loyalty_points_amount || 0);
          next.push(
            createCustomerPaymentMode({
              name: PAYMENT_MODES.POINTS,
              amount: pointsAmount,
              icon: PersonalCart,
              id: SPECIAL_PAYMENT_MODE_ID.POINTS,
              orderKey: PAYMENT_MODE_ORDER_KEYS.POINTS,
              defaultOrder: DEFAULT_PAYMENT_MODE_ORDER.POINTS,
              orderObj,
            }),
          );
        }
        return next;
      });
    } else {
      setPaymentModes((prev) => prev.filter((m) => m.name !== PAYMENT_MODES.CREDIT && m.name !== PAYMENT_MODES.POINTS));
    }
  }, [
    activeSale?.customer,
    activeSale?.payment_modes,
    activeSale?.customer_credit_used,
    activeSale?.loyalty_points_amount,
    selectedShop?.config?.payment_mode_order,
  ]);

  // Sync printSale when activeSale updates (from external waiter app)
  useEffect(() => {
    if (activeSale) {
      setPrintSale(activeSale);
    }
  }, [activeSale]);

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        {/* Header Row */}
        <div className={styles.summarySection}>
          {activeCustomerDetails ? (
            <div className={styles.customerCard}>
              <div className={styles.customerContent}>
                <h4>Customer</h4>
                <h2>{activeCustomerDetails.name}</h2>
              </div>
              <div className={styles.customerContent}>
                <h4>Mobile No</h4>
                <h2>{activeCustomerDetails.mobile}</h2>
              </div>
              <div className={styles.customerContent}>
                <h4>Group </h4>
                <h2>{activeCustomerDetails.group?.name ?? "-"}</h2>
              </div>
              <div className={styles.customerContent}>
                <h4>Credit Limit</h4>
                <h2>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.customerAmountFormat(activeCustomerDetails.credit_limit)}
                </h2>
              </div>
              <div className={styles.customerContent}>
                <h4>Balance</h4>
                <h2>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.customerAmountFormat(activeCustomerDetails.balance)}
                </h2>
              </div>
              <div className={styles.customerContent}>
                <h4>Loyalty Card</h4>
                <h2>{activeCustomerDetails.loyalty_card_no ?? "-"}</h2>
              </div>
              <div className={styles.customerContent}>
                <h4>Loyalty Point</h4>
                <h2>
                  {activeCustomerDetails?.points
                    ? `${activeCustomerDetails.points} × ${selectedShop?.loyalty_point_conversion_rate || 1} = ${roundOff(
                        activeCustomerDetails.points * (selectedShop?.loyalty_point_conversion_rate || 1),
                      )}`
                    : 0}
                </h2>
              </div>
            </div>
          ) : (
            ""
          )}
          <div className={styles.headerRow}>
            <div className={styles.headerContent}>
              <h5>Bill Total</h5>
              <h2>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(activeSale?.payable || 0)}
              </h2>
            </div>
            <div className={styles.headerContent}>
              <h5>Due Amount</h5>
              <h2>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(due || 0)}
              </h2>
            </div>
            <div className={styles.headerContent}>
              <h5>Pre Paid</h5>
              <h2>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(activeSale?.prepaid || 0)}
              </h2>
            </div>
            {activeSale?.totalChargeAmount > 0 && (
              <div className={styles.headerContent}>
                <h5>Extra Charge</h5>
                <h2 style={{ color: "#2b2b2b" }}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.roundOff(activeSale?.totalChargeAmount || 0)}
                </h2>
              </div>
            )}
            {activeSale?.discount_applied_on_items > 0 && (
              <div className={styles.headerContent}>
                <h5>Extra Discount</h5>
                <h2 style={{ color: "#e50218" }}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>-{" "}
                  {NumberFormatHelper.roundOff(activeSale?.discount_applied_on_items || 0)}
                </h2>
              </div>
            )}
            <div className={styles.headerContent}>
              <h5>Tender</h5>
              <h2>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(tender || 0)}
              </h2>
            </div>
            <div className={styles.headerContent}>
              <h5>Bill Discount</h5>
              <h2>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(discount || 0)}
              </h2>
            </div>
            {activeSale?.roundOff !== 0 && (
              <div className={styles.headerContent}>
                <h5>Round Off</h5>
                <h2>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {NumberFormatHelper.roundOff(activeSale?.roundOff || 0)}
                </h2>
              </div>
            )}
            <div className={styles.headerContent}>
              <h5>Change</h5>
              <h2>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {NumberFormatHelper.roundOff(change) || 0}
              </h2>
            </div>
          </div>
        </div>

        <div className={styles.paymentSection}>
          {/* Payment Tabs */}
          <div ref={paymentTabsRef} className={styles.paymentTabs}>
            {sortedPaymentModes.map((mode, index) => (
              <button
                key={mode.name}
                onClick={() => handlePaymentModeSelect(mode)}
                onMouseDown={(e) => e.preventDefault()}
                tabIndex={-1}
                className={`${index === 0 ? styles.firstBtn : ""} ${
                  index === sortedPaymentModes.length - 1 ? styles.lastBtn : ""
                } ${mode.name === activeMode ? styles.activeTab : ""}`}
              >
                <div className={styles.paymentHead}>
                  {mode.icon && <img src={mode.icon} alt={mode.name} className={styles.paymentIcon} />}
                  <h3 className={styles.label}>{mode.name}</h3>
                </div>

                <h2 className={styles.amount}>
                  {mode.id === SPECIAL_PAYMENT_MODE_ID.DISCOUNT_PERCENTAGE ? (
                    <span>{NumberFormatHelper.roundOff(mode.amount)}%</span>
                  ) : (
                    <>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {NumberFormatHelper.roundOff(mode.amount)}
                    </>
                  )}
                </h2>
                {mode.name === PAYMENT_MODES.POINTS && mode.amount > 0 && (
                  <div className={styles.pointsCalculation}>
                    {NumberFormatHelper.roundOff(
                      Number(mode.amount) / Number(selectedShop?.loyalty_point_conversion_rate || 1),
                    )}{" "}
                    X {selectedShop?.loyalty_point_conversion_rate} = {mode.amount}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Keypad Section */}
          <div className={styles.keypad}>
            {/* Row 1 */}
            <button
              className={styles.keyBtn}
              style={{ borderTopLeftRadius: "20px" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.ONE)}
            >
              1
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.TWO)}>
              2
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.THREE)}>
              3
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_TEN)}>
              +10
            </button>
            <button
              className={styles.keyBtn}
              style={{ borderTopRightRadius: "20px" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_HUNDERD)}
            >
              +100
            </button>

            {/* Row 2 */}
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.FOUR)}>
              4
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.FIVE)}>
              5
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.SIX)}>
              6
            </button>
            <button
              className={`${styles.keyBtn} ${styles.numeric}`}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_FIFTY)}
            >
              +50
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_TWO_HUNDERD)}>
              +200
            </button>

            {/* Row 3 */}
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.SEVEN)}>
              7
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.EIGHT)}>
              8
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.NINE)}>
              9
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_MINUS)}>
              +/-
            </button>
            <button
              className={`${styles.keyBtn} ${styles.numeric}`}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_FIVE_HUNDRED)}
            >
              +500
            </button>

            {/* Row 4 */}
            <button
              className={styles.keyBtn}
              style={{ borderBottomLeftRadius: "20px", backgroundColor: "rgba(226, 225, 228, 1)" }}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.CLEAR)}
            >
              <img src={BackIcon} alt="Back" style={styles.icon} />
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.ZERO)}>
              0
            </button>
            <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.DOT)}>
              .
            </button>
            <button
              className={styles.keyBtn}
              onClick={() => handleKeypadInput(KEYPAD_BUTTONS.DELETE)}
              style={{ backgroundColor: "rgba(226, 225, 228, 1)" }}
            >
              Delete
            </button>
            <button
              className={`${styles.keyBtn} ${styles.numeric}`}
              style={{ borderBottomRightRadius: "20px" }}
              onClick={handleAutoFillDue}
            >
              {roundOff(due)}
            </button>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.buttonSection}>
              <div className={styles.balanceText}>
                Pay balance :
                <span style={{ color: "#e50218", display: "flex" }}>
                  <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                  {roundOff(change) || 0}
                </span>{" "}
                Via
                <span style={{ color: "#e50218" }}>{balancePaymentModeLabel}</span>
                <button className={styles.changeBtn} onClick={() => setShowBalanceDialog(true)} disabled={change <= 0}>
                  Change
                </button>
              </div>

              {!isEditingPaymentModeOnly && (
                <>
                  {ShopsHelper.isPremiumShop(selectedShop) && (
                    <>
                      <div className={styles.separator}></div>
                      <div
                        className={styles.balanceText}
                        style={{ position: "relative" }}
                        ref={priceCategoryDropdownRef}
                      >
                        Price Category :
                        <button
                          className={styles.changeBtn}
                          onClick={() => setShowPriceCategoryDropdown((prev) => !prev)}
                          style={{ minWidth: "100px" }}
                        >
                          {SalesHelper.getActiveSalePriceCategory(contextActiveSale, selectedPriceCategory)?.name ||
                            "Default"}
                        </button>
                        {showPriceCategoryDropdown && (
                          <div className={styles.moreMenu} style={{ bottom: "40px", left: "0", right: "auto" }}>
                            <div
                              className={styles.menuItem}
                              onClick={() => {
                                handleChangePriceCategory(null);
                                setShowPriceCategoryDropdown(false);
                              }}
                            >
                              <h3>Default</h3>
                            </div>
                            {priceCategories.map((priceCategory) => (
                              <div
                                key={priceCategory.id}
                                className={styles.menuItem}
                                onClick={() => {
                                  handleChangePriceCategory(priceCategory);
                                  setShowPriceCategoryDropdown(false);
                                }}
                              >
                                <h3>{priceCategory.name}</h3>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={styles.separator}></div>
                      <div className={styles.balanceText} style={{ position: "relative" }} ref={chargeGroupDropdownRef}>
                        Charge group :
                        <button
                          className={styles.changeBtn}
                          onClick={() => setShowChargeGroupDropdown((prev) => !prev)}
                          style={{ minWidth: "100px" }}
                        >
                          {chargeGroupsAppliedForSale?.length > 0
                            ? chargeGroupsAppliedForSale.map((chargeGroup) => chargeGroup.name).join(", ")
                            : "Default"}
                        </button>
                        {showChargeGroupDropdown && (
                          <div className={styles.moreMenu} style={{ bottom: "40px", left: "0", right: "auto" }}>
                            <div
                              className={styles.menuItem}
                              onClick={() => {
                                addChargeGroupToActiveSale(null);
                                setShowChargeGroupDropdown(false);
                              }}
                            >
                              <h3>Default</h3>
                            </div>
                            {chargeGroups.map((chargeGroup) => {
                              const isSelected = chargeGroupsAppliedForSale.some((g) => g.id === chargeGroup.id);
                              return (
                                <div
                                  key={chargeGroup.id}
                                  className={styles.menuItem}
                                  onClick={() => {
                                    addChargeGroupToActiveSale(chargeGroup);
                                  }}
                                >
                                  <Checkbox checked={isSelected} size="small" sx={{ p: 0, mr: 1 }} />
                                  <h3>{chargeGroup.name}</h3>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {hasCustomerSupport && (
                    <>
                      <div className={styles.separator}></div>
                      {activeSale?.customer ? (
                        <div
                          className={styles.customerBtn}
                          onClick={() => {
                            removeCustomerFromSaleLocal();
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <img src={PersonalCart} alt="customer" className={styles.paymentIcon} />
                          <h3> Remove Customer</h3>
                        </div>
                      ) : (
                        <div style={{ position: "relative" }}>
                          <div className={styles.customerBtn} onClick={() => setShowCustomerSelector((prev) => !prev)}>
                            <img src={PersonalCart} alt="customer" className={styles.paymentIcon} />
                            <h3>Add Customer</h3>
                          </div>
                          {showCustomerSelector && (
                            <div className={styles.customerPopup}>
                              <CustomerSelector
                                open={showCustomerSelector}
                                onClose={() => setShowCustomerSelector(false)}
                                onSelect={handleCustomerSelection}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {!isEditingPaymentModeOnly && (
              <div className={styles.orderTypeRow}>
                {orderTypes.length > 1 &&
                  orderTypes.map((type) => (
                    <button
                      key={type}
                      className={`${styles.orderTypeBtn} ${activeOrderType === type ? styles.activeOrderType : ""}`}
                      onClick={() => handleOrderTypeChange(type)}
                    >
                      {type}
                    </button>
                  ))}
              </div>
            )}

            <div className={styles.footerBtns}>
              <div
                className={`${styles.closeBtn} ${isFinalizing ? styles.disabled : ""}`}
                onClick={() => {
                  if (isFinalizing) return;
                  handleClose();
                }}
              >
                <img src={CloseIcon} alt="Close Icon" style={{ width: "20px" }} />
                Close
              </div>
              <CheckoutActionMenu
                className={styles.actionMenuContainer}
                onDefaultActionClick={createSale(TerminalHelper.getDefaultPrintAction())}
                onPrintClick={createSale(INVOICE_ACTIONS.PRINT)}
                onSaveInvoice={createSale(INVOICE_ACTIONS.SAVE)}
                onSaveAndWhatsApp={createSale(INVOICE_ACTIONS.SAVE_AND_WHATSAPP)}
                onPrintAndWhatsApp={createSale(INVOICE_ACTIONS.PRINT_AND_WHATSAPP)}
                onPaymentQr={displayPaymentQr}
                onKotAndPrint={createSale(INVOICE_ACTIONS.WITH_KOT)}
                disabled={isFinalizing}
                isLoading={isFinalizing}
                buttonLabel={InvoiceActionHelper.getInvoiceActionButtonLabel()}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: "-10000px",
          top: "-10000px",
          visibility: "hidden",
        }}
      >
        {InvoiceComponent}
        <KOTInvoice ref={kotPrintRef} sale={kotData} shop={selectedShop} />
      </div>

      <PermissionDialog />

      {showBalanceDialog && (
        <BalanceDialog
          balance={change}
          paymentModes={sortedPaymentModes.filter((m) => m.name !== "Discount")}
          onSave={(data) => {
            setBalancePayments(data);
            setShowBalanceDialog(false);
          }}
          onClose={() => setShowBalanceDialog(false)}
        />
      )}

      {showPaymentQrDialog && (
        <GeneratePaymentQrDialog
          onClose={() => setShowPaymentQrDialog(false)}
          onSaveAndClose={handleQrPaymentReceived}
          sale={activeSale}
          shop={selectedShop}
          amount={upiAmount > 0 ? upiAmount : due > 0 ? due : activeSale?.payable}
        />
      )}

      {showDescriptionDialog && (
        <EditSaleDescriptionDialog
          open={showDescriptionDialog}
          onClose={handleCloseDescriptionDialog}
          onConfirm={handleConfirmDescription}
          sale={activeSale}
        />
      )}
    </div>
  );
}

export default SaleEditDialog;
